// id landing pages: see config/localeHubs.ts for the rules and
// config/hubs/id.json for the slug of each page type (keep them in step).
//
// Indonesian, casual "kamu". Volumes are OpenSEO, Indonesia, monthly
// (Oct 2026); phrasing from Google autocomplete (hl=id, gl=id).
// - Skipped on purpose: "suara ai" (6.6k) returns text-to-speech results.
// - Voice-changer autocomplete is full of "whatsapp", "saat telepon",
//   "di game ff" and "real time": answered honestly in the FAQ.
// - "gratis" is appended to nearly every term: answered in the FAQ, never
//   promised.
import type { HubCategory, HubConfig } from "../hubPages";

const CATEGORIES_ID: HubCategory[] = [
  { label: "Musik", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Kartun", filter: "cartoon" },
  { label: "Game", filter: "gaming" },
  { label: "K-pop", filter: "korean" },
  { label: "Tokoh terkenal", filter: "public figure" },
];

const FREE_ID = {
  q: "Apakah Jammable gratis?",
  a: "Menjelajahi semua suara dan mendengarkan contoh cover itu gratis. Untuk bikin cover, mengubah suaramu, clone suara, atau memakai teks ke suara, kamu butuh paket. Cek halaman harga untuk pilihan yang tersedia saat ini.",
};

export const HUBS: Record<string, HubConfig> = {
  // "pengubah suara" 2,900 (KD 12), "aplikasi pengubah suara" 1,600,
  // "pengubah suara ai" 720, "ubah suara" 720. Autocomplete: "pengubah suara
  // ai anime", "ubah suara jadi wanita", "ubah suara cowo ke cewe",
  // "ai pengubah suara menjadi suara orang lain", "pengubah suara video".
  "pengubah-suara-ai": {
    slug: "pengubah-suara-ai",
    group: "voice-changer",
    path: "/id/pengubah-suara-ai",
    locale: "id",
    h1: "Pengubah suara AI",
    highlight: "suara AI",
    title: "Pengubah Suara AI: Ubah Suaramu Jadi Suara Siapa Pun | Jammable",
    metaDescription:
      "Pengubah suara AI online: ubah suaramu jadi suara karakter anime, penyanyi, atau tokoh terkenal. 60.000+ suara realistis. Rekam atau upload audio, tanpa install aplikasi.",
    heroSub:
      "Rekam atau upload audio, pilih ingin terdengar seperti siapa, dan dengarkan suaramu berubah dalam hitungan detik.",
    intro:
      "Pengubah suara AI bukan sekadar efek yang bikin suaramu cempreng atau berat. AI mengubahnya jadi suara lain yang terdengar asli: karakter anime, penyanyi, tokoh terkenal, atau suara yang kamu latih sendiri. Langsung di browser, tanpa perlu aplikasi.",
    appName: "Jammable Pengubah Suara AI",
    steps: [
      {
        title: "Rekam atau upload",
        body: "Rekam suaramu lewat mikrofon atau upload audio yang ingin kamu ubah.",
      },
      {
        title: "Pilih suara",
        body: "60.000+ suara AI, dari karakter anime dan kartun sampai penyanyi dan tokoh terkenal.",
      },
      {
        title: "Ubah suaramu",
        body: "Dalam hitungan detik audiomu sudah pakai suara baru, siap di-download.",
      },
    ],
    faqs: [
      {
        q: "Apa itu pengubah suara AI?",
        a: "Alat yang mengambil audio rekaman lalu mengubahnya agar terdengar seperti suara lain, memakai konversi suara AI. Beda dengan aplikasi pengubah suara berbasis efek, hasilnya terdengar seperti suara asli yang kamu pilih.",
      },
      {
        q: "Bagaimana cara ubah suara dengan AI?",
        a: "Rekam atau upload audio, pilih suara yang kamu mau, lalu tekan buat. Jammable mengubah audiomu dan memberikan hasil yang bersih untuk di-download.",
      },
      {
        q: "Apakah bisa real time, untuk telepon, WhatsApp, atau main game?",
        a: "Tidak. Jammable mengubah audio yang direkam atau diunggah, bukan mikrofon langsung atau panggilan. Karena itu hasilnya jauh lebih realistis daripada pengubah suara real time. Setelah jadi, kamu bisa download audionya dan kirim ke mana saja.",
      },
      {
        q: "Bisakah suaraku diubah jadi suara karakter anime atau suara cewek?",
        a: "Bisa. Ada ribuan suara karakter anime, kartun, dan game, serta suara penyanyi pria dan wanita. Suara baru ditambahkan komunitas setiap hari.",
      },
      {
        q: "Apakah perlu download aplikasi pengubah suara?",
        a: "Tidak perlu. Jammable berjalan di browser, baik di HP maupun laptop. Cukup buka situsnya, rekam atau upload audio, lalu pilih suara.",
      },
      FREE_ID,
    ],
    categories: CATEGORIES_ID,
    related: [
      { label: "Cover lagu AI", href: "/id/cover-lagu-ai" },
      { label: "Clone suara AI", href: "/id/clone-suara-ai" },
      { label: "Semua suara AI", href: "/models" },
    ],
  },

  // "cover ai" 1,300 (KD 0), "ai cover" 1,300 (KD 47), "cover lagu ai" 720,
  // "ai cover lagu" 480. Autocomplete: "cover lagu ai pakai suara sendiri",
  // "cara cover lagu pakai ai", "ai cover lagu gratis", "cover lagu ai
  // indonesia", "ai pengubah suara lagu menjadi suara kita".
  "cover-lagu-ai": {
    slug: "cover-lagu-ai",
    group: "cover",
    path: "/id/cover-lagu-ai",
    locale: "id",
    h1: "Cover lagu AI dengan suara apa pun",
    highlight: "AI",
    title: "Cover AI: Bikin Cover Lagu AI dengan Suara Apa Pun | Jammable",
    metaDescription:
      "Bikin AI cover lagu dengan 60.000+ suara: penyanyi, karakter anime, dan tokoh terkenal. Ganti suara di audio dengan suara lain dalam hitungan detik, kualitas studio.",
    heroSub:
      "Pilih suara, tambahkan audio yang boleh kamu gunakan, dan Jammable mengembalikan cover AI dalam hitungan detik.",
    intro:
      "Dulu, mengganti suara penyanyi di sebuah lagu butuh studio dan berjam-jam mixing. Dengan Jammable kamu tinggal pilih suara, upload audiomu, dan AI mengerjakan sisanya: memisahkan vokal, mengubah suaranya, lalu mixing ulang.",
    appName: "Jammable Cover Lagu AI",
    steps: [
      {
        title: "Pilih suara",
        body: "60.000+ suara AI: penyanyi, rapper, karakter kartun dan anime, atau suara yang kamu latih sendiri.",
      },
      {
        title: "Tambahkan audiomu",
        body: "Upload file, rekam dirimu bernyanyi, atau buat lagu orisinal dengan AI.",
      },
      {
        title: "Buat covermu",
        body: "Jammable mengubah suaranya dan mixing hasilnya. Download lalu bagikan ke TikTok atau YouTube.",
      },
    ],
    faqs: [
      {
        q: "Apa itu cover AI?",
        a: "Versi sebuah lagu yang dinyanyikan dengan suara lain berkat AI. Kamu ambil sebuah track atau suaramu sendiri, lalu mengubahnya jadi suara artis, karakter, atau suara yang kamu latih, dengan melodi dan lirik yang tetap sama.",
      },
      {
        q: "Bagaimana cara cover lagu pakai AI?",
        a: "Pilih suara di Jammable, upload audionya, lalu tekan buat. Jammable memisahkan vokal dari musik, mengubahnya ke suara pilihanmu, dan mixing ulang semuanya dalam waktu kurang dari semenit. Gunakan hanya audio yang boleh kamu pakai.",
      },
      {
        q: "Bisakah bikin cover lagu AI pakai suara sendiri?",
        a: "Bisa. Latih model dengan suaramu dari minimal 6 menit audio yang bersih, lalu pakai di cover mana pun.",
      },
      FREE_ID,
      {
        q: "Seberapa bagus hasil cover AI?",
        a: "Bisa terdengar sangat mirip rekaman asli. Jammable memakai konversi suara berkualitas tinggi dan mixing otomatis; paket berbayar punya pengaturan kualitas yang lebih tinggi lagi.",
      },
    ],
    categories: CATEGORIES_ID,
    related: [
      { label: "Pengubah suara AI", href: "/id/pengubah-suara-ai" },
      { label: "Clone suara AI", href: "/id/clone-suara-ai" },
      { label: "Semua suara AI", href: "/models" },
    ],
  },

  // "clone suara ai" 70. Kept short: autocomplete runs deep on two spellings
  // ("clone suara ai gratis", "clone suara sendiri", "clone suara orang
  // lain"; "kloning suara ai", "kloning suara ai indonesia gratis"), and many
  // of them name politicians, so the consent line matters here.
  "clone-suara-ai": {
    slug: "clone-suara-ai",
    group: "clone",
    path: "/id/clone-suara-ai",
    locale: "id",
    h1: "Clone suara AI",
    highlight: "suara AI",
    title: "Clone Suara AI: Bikin Model Suaramu Sendiri | Jammable",
    metaDescription:
      "Clone atau kloning suaramu dengan AI dari 6 menit audio. Pakai suara hasil clone untuk cover, ubah audio, dan teks ke suara. Bisa juga upload model RVC sendiri.",
    heroSub:
      "Upload minimal 6 menit audio yang bersih dan Jammable melatih model suara itu untuk dipakai di cover dan teks ke suara.",
    intro:
      "Clone suara dengan AI artinya melatih model yang mempelajari bagaimana sebuah suara terdengar. Setelah itu kamu bisa membuat suara tersebut bernyanyi atau berbicara apa pun. Clone hanya suaramu sendiri atau suara yang sudah diizinkan pemiliknya.",
    appName: "Jammable Clone Suara AI",
    steps: [
      {
        title: "Upload audiomu",
        body: "Minimal 6 menit suara nyanyian atau bicara, bersih dan tanpa jeda hening yang panjang.",
      },
      {
        title: "Latih modelnya",
        body: "Jammable melatih suaranya untukmu. Tidak perlu kartu grafis atau keahlian teknis.",
      },
      {
        title: "Pakai suaramu",
        body: "Bikin cover, ubah audio, atau ketik teks dan dengarkan dengan suara hasil clone-mu.",
      },
    ],
    faqs: [
      {
        q: "Bagaimana cara clone suara dengan AI?",
        a: "Upload minimal 6 menit audio bersih dari suara tersebut, beri nama, lalu Jammable melatih modelnya. Setelah selesai, suaranya muncul di pustakamu dan siap dipakai.",
      },
      {
        q: "Butuh berapa banyak audio untuk kloning suara?",
        a: "Minimal 6 menit suara yang bersih, tanpa musik latar atau jeda hening panjang. Makin banyak dan makin bagus audionya, hasilnya makin mirip.",
      },
      {
        q: "Bisakah aku upload model RVC yang sudah kupunya?",
        a: "Bisa. Upload file .pth dan .index dari pustakamu, dan suaranya langsung siap dipakai.",
      },
      {
        q: "Bolehkah aku clone suara orang lain?",
        a: "Hanya kalau kamu punya izin dari orangnya. Clone suaramu sendiri atau suara yang memang boleh kamu gunakan, dan jangan memakainya untuk menipu siapa pun.",
      },
      FREE_ID,
    ],
    categories: CATEGORIES_ID,
    related: [
      { label: "Latih suaraku", href: "/train" },
      { label: "Cover lagu AI", href: "/id/cover-lagu-ai" },
      { label: "Pengubah suara AI", href: "/id/pengubah-suara-ai" },
    ],
  },
};
